import type { DuplexStream, TransportConnectionConfiguration, Transport } from '@ts-ssh2/core';

/**
 * Transport that connects over WebSocket through a relay gateway.
 *
 * The constructor receives the gateway base URL. When `connect()` is called
 * with an SSH target, the transport appends `/relay/<hostname>/<port>` to
 * form the full WebSocket URL. This means the `hostname` and `port` in
 * `SshConnectionConfiguration` refer to the actual SSH server — the transport
 * handles routing through the gateway transparently.
 *
 * Usage:
 *   const transport = new WebSocketTransport('ws://gateway.example.com:3000');
 *   await client.connect({
 *     transport,
 *     hostname: 'internal-server.local',
 *     port: 22,
 *     user: 'admin',
 *     privateKey: '...',
 *   });
 *
 * Or with a pre-existing WebSocket:
 *   const ws = new WebSocket('ws://gateway.example.com:3000/relay/server/22');
 *   const stream = await WebSocketTransport.fromWebSocket(ws);
 *   await client.connect({ stream, user: 'admin', privateKey: '...' });
 */
export class WebSocketTransport implements Transport {
	private baseUrl: string;

	constructor(baseUrl: string) {
		// strip trailing slash for clean path concatenation
		this.baseUrl = baseUrl.replace(/\/+$/, '');
	}

	async connect(
		target: TransportConnectionConfiguration,
		signal?: AbortSignal,
	): Promise<DuplexStream> {
		signal?.throwIfAborted();

		const url = `${this.baseUrl}/relay/${target.hostname}/${target.port}`;

		const ws = new WebSocket(url);
		ws.binaryType = 'arraybuffer';

		await waitForOpen(ws, signal);

		return wrapWebSocket(ws);
	}

	/**
	 * Create a DuplexStream from an already-open WebSocket.
	 *
	 * The WebSocket must be in the OPEN state or about to open.
	 * This is useful when the caller manages the WebSocket lifecycle
	 * (e.g. in a browser with custom headers/auth).
	 *
	 * The returned DuplexStream can be passed directly to Client.connect()
	 * via the `stream` config option.
	 */
	static async fromWebSocket(ws: WebSocket, signal?: AbortSignal): Promise<DuplexStream> {
		ws.binaryType = 'arraybuffer';

		if (ws.readyState === WebSocket.CONNECTING) {
			await waitForOpen(ws, signal);
		} else if (ws.readyState !== WebSocket.OPEN) {
			throw new Error(`WebSocket is not open (readyState=${ws.readyState})`);
		}

		return wrapWebSocket(ws);
	}
}

/**
 * Wait for a WebSocket to reach the OPEN state.
 */
function waitForOpen(ws: WebSocket, signal?: AbortSignal): Promise<void> {
	if (ws.readyState === WebSocket.OPEN) {
		return Promise.resolve();
	}

	return new Promise<void>((resolve, reject) => {
		const cleanup = () => {
			ws.removeEventListener('open', onOpen);
			ws.removeEventListener('error', onError);
			ws.removeEventListener('close', onClose);
			signal?.removeEventListener('abort', onAbort);
		};

		const onOpen = () => {
			cleanup();
			resolve();
		};

		const onError = () => {
			cleanup();
			reject(new Error('WebSocket connection failed'));
		};

		const onClose = (event: CloseEvent) => {
			cleanup();
			reject(new Error(`WebSocket closed before opening: code=${event.code}`));
		};

		const onAbort = () => {
			cleanup();
			ws.close();
			reject(signal!.reason);
		};

		ws.addEventListener('open', onOpen, { once: true });
		ws.addEventListener('error', onError, { once: true });
		ws.addEventListener('close', onClose, { once: true });
		signal?.addEventListener('abort', onAbort, { once: true });
	});
}

/**
 * Wrap an open WebSocket into a DuplexStream.
 */
function wrapWebSocket(ws: WebSocket): DuplexStream {
	const readable = new ReadableStream<Uint8Array>({
		start(controller) {
			ws.addEventListener('message', (event: MessageEvent) => {
				const data = event.data;
				if (data instanceof ArrayBuffer) {
					controller.enqueue(new Uint8Array(data));
				} else if (data instanceof Uint8Array) {
					controller.enqueue(data);
				}
				// string data is ignored — SSH is binary only
			});

			ws.addEventListener('close', () => {
				try {
					controller.close();
				} catch {
					// already closed
				}
			});

			ws.addEventListener('error', () => {
				try {
					controller.error(new Error('WebSocket error'));
				} catch {
					// already errored/closed
				}
			});
		},
		cancel() {
			ws.close();
		},
	});

	const writable = new WritableStream<Uint8Array>({
		write(chunk) {
			if (ws.readyState !== WebSocket.OPEN) {
				throw new Error('WebSocket is not open');
			}
			// send as binary (ArrayBuffer)
			ws.send(chunk);
		},
		close() {
			ws.close();
		},
		abort() {
			ws.close();
		},
	});

	return { readable, writable };
}
